import AxiosInstance from '@/api/axios';
import { toast } from '@/components/Base/Toast/ToastManager';
import { TemplateRegisterInputs } from '@/components/UpdateTemplate';
import { getClientId } from '@/utils/helper';
import { TemplateAgent } from '@/utils/templateAgents';
import { CustomError, Template, TemplatesDetailsType } from '@/utils/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const fetchTemplates = createAsyncThunk(
  'fetchTemplates/fetchData',
  async (
    {
      clientId,
      branchId,
      regionId,
      withNoBranch,
      agentType,
      withDetails,
    }: {
      clientId: string;
      branchId: string;
      regionId: string;
      withNoBranch: boolean | undefined;
      // Only this agent's templates; omit for every agent's.
      agentType?: TemplateAgent;
      // Templates page: active status and who created / edited each.
      withDetails?: boolean;
    },
    thunkAPI
  ) => {
    try {
      if (branchId === 'all') return;
      const response = await AxiosInstance.get(`/templates/all/${clientId}`, {
        params: {
          branch: branchId,
          region: regionId,
          withNoBranch,
          agentType,
          withDetails,
        },
      });

      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchBulkTemplates = createAsyncThunk(
  'fetchBulkTemplates/fetchData',
  async (
    {
      clientId,
      regionId,
    }: {
      clientId: string;
      regionId?: string;
    },
    thunkAPI
  ) => {
    try {
      const params: Record<string, string> = { whatsappCollectionOnly: 'true' };
      if (regionId) {
        params.region = regionId;
      }
      const response = await AxiosInstance.get(`/templates/all/${clientId}`, {
        params,
      });

      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Adds one template for one agent. Use this rather than `updateTemplates`
 * when the store holds a single agent's templates, so the other agents'
 * templates are never resent (and overwritten).
 */
export const addTemplateItem = createAsyncThunk(
  'addTemplateItem/fetchData',
  async (
    {
      templateId,
      channel,
      agentType,
      branch,
      region,
      template,
    }: {
      templateId?: string;
      channel: string;
      agentType: TemplateAgent;
      branch: string;
      region: string;
      template: Template;
    },
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.post('/templates/items', {
        ...template,
        client: getClientId(),
        templateId,
        channel,
        agentType,
        branch,
        region,
      });
      return response.data.data as { templateId: string; template: Template };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateTemplates = createAsyncThunk(
  'updateTemplates/fetchData',
  async (
    {
      templateId,
      data,
    }: { templateId: string | undefined; data: TemplatesDetailsType },
    thunkAPI
  ) => {
    const clientId = getClientId();
    try {
      const response = await AxiosInstance.patch(`/templates/${templateId}`, {
        ...data,
        client: data.client?._id ? data.client?._id : clientId,
      });

      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateTemplatesV2 = createAsyncThunk(
  'updateTemplatesV2/fetchData',
  async (
    {
      data,
      branch,
      region,
      agentType,
    }: {
      data: TemplateRegisterInputs;
      branch: string;
      region: string;
      agentType?: TemplateAgent;
    },
    thunkAPI
  ) => {
    const clientId = getClientId();
    try {
      const response = await AxiosInstance.patch(
        `/templates/v2/${data._id}/client/${clientId}`,
        {
          ...data,
          branch,
          region,
          agentType,
        }
      );

      if (response.data.success) {
        toast.show({
          title: 'Changes saved successfully.',
          content: 'Template Updated',
          duration: 2000,
          type: 'primary',
        });
        return response.data.data;
      }
      return null;
    } catch (error) {
      const IError = error as CustomError;
      toast.show({
        title: 'Operation Failed',
        content: IError?.response?.data?.message as string,
        duration: 2000,
        type: 'warning',
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'deleteTemplate/fetchData',
  async ({ templateId }: { templateId: string }, thunkAPI) => {
    try {
      const client = getClientId();
      const response = await AxiosInstance.delete(
        `/templates/${templateId}/${client}`
      );

      if (response.data.success) {
        toast.show({
          title: 'Changes saved successfully.',
          content: 'Template deleted',
          duration: 2000,
          type: 'primary',
        });
        return { templateId };
      }
    } catch (error) {
      const IError = error as CustomError;
      toast.show({
        title: 'Operation Failed',
        content: IError?.response?.data?.message as string,
        duration: 2000,
        type: 'warning',
      });
    }
  }
);

interface TemplatesStateType {
  data: TemplatesDetailsType | null | undefined;
  loading: boolean;
  error: string | null;
  // Only the latest fetch may set `data`: switching agent or location fires
  // several requests, and an older response must not overwrite a newer one.
  latestFetchId: string | null;
}

const initialState: TemplatesStateType = {
  data: null,
  loading: false,
  error: null,
  latestFetchId: null,
};

const templatesReducer = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.latestFetchId = action.meta.requestId;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestFetchId) return;
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBulkTemplates.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.latestFetchId = action.meta.requestId;
      })
      .addCase(fetchBulkTemplates.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestFetchId) return;
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchBulkTemplates.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addTemplateItem.fulfilled, (state, action) => {
        const { templateId, template } = action.payload;
        const field = `${action.meta.arg.channel}Template` as
          | 'emailTemplate'
          | 'smsTemplate'
          | 'whatsappTemplate'
          | 'viberTemplate';
        if (!state.data) return;
        state.data._id = templateId;
        state.data[field] = [...(state.data[field] ?? []), template];
      })
      .addCase(updateTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.loading = false;

        // Remove the deleted template from the state
        if (state.data) {
          const { templateId } = action.payload as { templateId: string };
          const { emailTemplate, whatsappTemplate, smsTemplate, viberTemplate } =
            state.data;

          state.data.emailTemplate = emailTemplate?.filter(
            (template) => template._id !== templateId
          );
          state.data.whatsappTemplate = whatsappTemplate?.filter(
            (template) => template._id !== templateId
          );
          state.data.smsTemplate = smsTemplate?.filter(
            (template) => template._id !== templateId
          );
          state.data.viberTemplate = viberTemplate?.filter(
            (template) => template._id !== templateId
          );
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTemplatesV2.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplatesV2.fulfilled, (state, action) => {
        state.loading = false;

        if (!state.data) return;

        const updatedTemplate = action.payload;

        const updateList = (list?: any[]) =>
          list?.map((template) =>
            template._id === updatedTemplate._id ? updatedTemplate : template
          );

        state.data.emailTemplate = updateList(state.data.emailTemplate);
        state.data.smsTemplate = updateList(state.data.smsTemplate);
        state.data.whatsappTemplate = updateList(state.data.whatsappTemplate);
        state.data.viberTemplate = updateList(state.data.viberTemplate);
      })
      .addCase(updateTemplatesV2.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default templatesReducer.reducer;
